import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ITConsultationAuditing = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920" 
          alt="IT Consultation" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Strategic <span className="text-green-500">IT Consultation & Auditing</span>
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Empower your organization with expert guidance and transparent insights. We help align your technology with business goals through strategic consulting and comprehensive audits.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop" 
              alt="IT Consultation" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Transform Your IT <span className="text-green-600">Strategy</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              At Plexgate, we provide strategic IT consultation and comprehensive auditing services to help organizations optimize their technology investments, improve security, and achieve business objectives. Our experienced consultants bring deep technical expertise and industry best practices to every engagement.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you need help developing an IT strategy, assessing your current infrastructure, ensuring compliance, or optimizing performance, our team provides actionable insights and practical recommendations that drive measurable results.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => {
    const services = [
      {
        title: 'IT Strategy Development',
        description: 'Align technology with business goals through strategic planning, roadmap development, and digital transformation initiatives.',
        icon: '🎯'
      },
      {
        title: 'Infrastructure Audits',
        description: 'Comprehensive assessment of your IT infrastructure, identifying vulnerabilities, inefficiencies, and improvement opportunities.',
        icon: '🔍'
      },
      {
        title: 'Security Audits',
        description: 'Detailed security assessments including penetration testing, vulnerability scanning, and compliance reviews.',
        icon: '🔒'
      },
      {
        title: 'Technology Consulting',
        description: 'Expert guidance on technology selection, vendor evaluation, system integration, and modernization projects.',
        icon: '💡'
      },
      {
        title: 'Compliance Assessment',
        description: 'Ensure regulatory compliance with GDPR, HIPAA, PCI-DSS, SOC 2, and industry-specific standards.',
        icon: '📋'
      },
      {
        title: 'Performance Optimization',
        description: 'Identify bottlenecks, optimize system performance, reduce costs, and improve operational efficiency.',
        icon: '⚡'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Consultation & Auditing</span> Services
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Expert guidance to optimize your IT investments and operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Audit Process Section
  const AuditProcessSection = () => {
    const steps = [
      {
        number: '01',
        title: 'Initial Assessment',
        description: 'Understanding your business objectives, current IT environment, pain points, and specific audit requirements.'
      },
      {
        number: '02',
        title: 'Data Collection',
        description: 'Gathering information through interviews, documentation review, system analysis, and technical assessments.'
      },
      {
        number: '03',
        title: 'Analysis & Findings',
        description: 'Comprehensive analysis of collected data, identifying gaps, risks, inefficiencies, and opportunities.'
      },
      {
        number: '04',
        title: 'Recommendations',
        description: 'Detailed report with actionable recommendations, prioritized roadmap, and implementation guidance.'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Audit <span className="text-green-600">Process</span>
            </h2>
            <p className="text-gray-600">A systematic approach to comprehensive IT assessment</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gray-50 rounded-lg p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // What We Assess Section
  const AssessmentAreasSection = () => {
    const areas = [
      { name: 'Network Infrastructure', icon: '🌐' },
      { name: 'Security Posture', icon: '🛡️' },
      { name: 'Data Management', icon: '💾' },
      { name: 'Cloud Architecture', icon: '☁️' },
      { name: 'Business Continuity', icon: '🔄' },
      { name: 'Compliance Status', icon: '✓' },
      { name: 'System Performance', icon: '⚡' },
      { name: 'Cost Efficiency', icon: '💰' },
      { name: 'User Experience', icon: '👥' },
      { name: 'Vendor Management', icon: '🤝' },
      { name: 'IT Governance', icon: '📊' },
      { name: 'Disaster Recovery', icon: '🆘' }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              What We <span className="text-green-600">Assess</span>
            </h2>
            <p className="text-gray-600">Comprehensive evaluation across all IT domains</p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {areas.map((area, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-3">{area.icon}</div>
                <h3 className="font-bold text-gray-900">{area.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Benefits Section
  const BenefitsSection = () => {
    const benefits = [
      {
        title: 'Expert Insights',
        description: 'Access to experienced consultants with deep technical knowledge and industry expertise.',
        icon: '👨‍💼'
      },
      {
        title: 'Objective Assessment',
        description: 'Unbiased, third-party evaluation of your IT environment without internal politics.',
        icon: '🎯'
      },
      {
        title: 'Cost Optimization',
        description: 'Identify opportunities to reduce IT costs while improving performance and capabilities.',
        icon: '💰'
      },
      {
        title: 'Risk Mitigation',
        description: 'Discover and address security vulnerabilities, compliance gaps, and operational risks.',
        icon: '🛡️'
      },
      {
        title: 'Strategic Roadmap',
        description: 'Clear action plan with prioritized recommendations aligned with business objectives.',
        icon: '🗺️'
      },
      {
        title: 'Competitive Advantage',
        description: 'Leverage technology more effectively to gain competitive edge in your industry.',
        icon: '🏆'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose Our <span className="text-green-600">Consulting Services</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{benefit.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Deliverables Section
  const DeliverablesSection = () => {
    const deliverables = [
      'Executive Summary with key findings',
      'Detailed Technical Assessment Report',
      'Risk Analysis and Priority Matrix',
      'Actionable Recommendations',
      'Implementation Roadmap with Timeline',
      'Cost-Benefit Analysis',
      'Compliance Gap Assessment',
      'Performance Metrics and KPIs',
      'Strategic Technology Roadmap',
      'Ongoing Support and Follow-up'
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              What You'll <span className="text-green-600">Receive</span>
            </h2>
            <p className="text-gray-600">Comprehensive documentation and actionable insights</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {deliverables.map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <span className="text-green-500 text-2xl mt-1">✓</span>
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Optimize Your <span className="text-green-500">IT Strategy</span>?
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Schedule a consultation with our experts and discover how we can help you achieve your technology goals.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Schedule Consultation →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="it-consultation-auditing-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <AuditProcessSection />
      <AssessmentAreasSection />
      <BenefitsSection />
      <DeliverablesSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};



  // FAQ Section
  const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
    const faqs = [
      {
        question: 'How long does an IT audit take?',
        answer: 'Timeline depends on scope and organization size. A basic audit takes 2-4 weeks, while comprehensive enterprise audits can take 6-12 weeks. We provide detailed timelines during initial consultation.'
      },
      {
        question: 'Will the audit disrupt our operations?',
        answer: 'We minimize disruption by scheduling activities during off-hours when needed and using non-intrusive assessment methods. Most audits have minimal impact on daily operations.'
      },
      {
        question: 'What happens after the audit?',
        answer: 'We provide a detailed report with findings and recommendations. We can also assist with implementing improvements, providing ongoing support, or conducting follow-up audits.'
      },
      {
        question: 'How much does IT consultation cost?',
        answer: 'Costs vary based on scope, duration, and complexity. We offer flexible pricing including project-based, hourly rates, and retainer options. Contact us for a customized quote.'
      },
      {
        question: 'Do you sign NDAs?',
        answer: 'Absolutely! We understand the sensitive nature of IT audits and gladly sign non-disclosure agreements to protect your confidential information.'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-green-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <span className="text-2xl text-gray-600">
                    {expandedFAQ === index ? '−' : '+'}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-white border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };


export default ITConsultationAuditing;